import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useFamily } from "@/hooks/useFamily";
import { useAllChildren } from "@/hooks/useAllChildren";
import { Skeleton } from "@/components/ui/skeleton";
import { BottomNav } from "@/components/BottomNav";
import { PwaUpdatePrompt } from "@/components/PwaUpdatePrompt";

/**
 * Centralized loading guard for all parent/app routes.
 * Preloads family + children data and shows a consistent skeleton
 * so individual pages never need their own top-level loading guards.
 */
export function AppShell() {
  const { isLoading: famLoading } = useFamily();
  const { isLoading: childrenLoading } = useAllChildren();

  useEffect(() => {
    // Ensure parent app views always start at the top after navigation/login
    const scrollEl = document.getElementById("app-scroll-container");
    if (scrollEl) scrollEl.scrollTop = 0;
    window.scrollTo(0, 0);

    document.body.setAttribute("data-role", "parent");
    return () => {
      document.body.removeAttribute("data-role");
    };
  }, []);

  if (famLoading || childrenLoading) {
    return (
      <div className="flex flex-col h-[100dvh]">
        <div className="flex-1 overflow-y-auto bg-background px-4 pt-safe pb-6">
          <div className="mx-auto max-w-sm space-y-6">
            <Skeleton className="h-8 w-32 mx-auto" />
            <Skeleton className="h-24 w-full rounded-xl" />
            <div className="space-y-3">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[100dvh]">
      <PwaUpdatePrompt hasBottomNav />
      <div className="flex-1 overflow-y-auto" id="app-scroll-container">
        <Outlet />
      </div>
      <BottomNav />
    </div>
  );
}
